import axios from 'axios';

// Base API URL - check if we're in development
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:6544/api' 
  : 'https://aluxim.pl/api';

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage
let authToken: string | null = null;

// Set auth token
export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem('gallery-admin-token', token);
  } else {
    localStorage.removeItem('gallery-admin-token');
  }
};

// Get auth token
export const getAuthToken = (): string | null => {
  if (!authToken) {
    authToken = localStorage.getItem('gallery-admin-token');
  }
  return authToken;
};

// Request interceptor for API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['x-admin-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle auth errors
    if (error.response?.status === 401) {
      setAuthToken(null);
      // Redirect to login if needed
    }
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Authentication
  login: (credentials: { login: string; password: string }) =>
    apiClient.post('/login', credentials),
  
  logout: () => apiClient.post('/logout'),
  
  checkAuth: () => apiClient.get('/check'),
  
  // Gallery
  getGallery: () => apiClient.get('/gallery'),
  
  addGalleryItem: (item: any) => apiClient.post('/gallery/add', item),
  
  updateGalleryItem: (id: number, item: any) => apiClient.put(`/gallery/${id}`, item),
  
  deleteGalleryItem: (id: number) => apiClient.delete(`/gallery/${id}`),
  
  // File upload
  uploadImages: (formData: FormData) => apiClient.post('/gallery/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Stats
  getStats: () => apiClient.get('/gallery/stats'),
  
  // Transformations
  getTransformations: () => apiClient.get('/transformations'),
  
  addTransformation: (item: any) => apiClient.post('/transformations/add', item),
  
  updateTransformation: (id: number, item: any) => apiClient.put(`/transformations/${id}`, item),
  
  deleteTransformation: (id: number) => apiClient.delete(`/transformations/${id}`),
  
  getTransformationStats: () => apiClient.get('/transformations/stats'),
  
  // Content Management
  getContent: () => apiClient.get('/content'),
  
  updateContent: (content: any) => apiClient.put('/content', content),
};

export default apiClient;
